import type { ChartDataPoint, ChartProps } from '@fluentui/react-charts';
import { DonutChart } from '@fluentui/react-charts';
import { List, ListItem, Text } from '@fluentui/react-components';
import { sumBy } from 'es-toolkit';
import { observer } from 'mobx-react-lite';
import React, { JSX } from 'react';
import { useStores } from '../../../common/hooks';
import { Cyan, DarkCyan, DarkGreen, DarkPurple, Green, LightBlue, LightCyan, LightGreen, LightPurple, MidDarkPurple, Purple } from '../../../common/types/color-types';
import { FlexBox } from '../../shared-components/flex-box';
import './AwardProgramCharts.scss';

const CHART_INNER_RADIUS = 40;
const CHART_WIDTH = 120;
const CHART_HEIGHT = 120;

enum ChartLegend {
  CategoryNominations = 'Category nominations',
  CountryNominations = 'Country nominations',
  RegionNominations = 'Region nominations',
  TotalSubmitted = 'Total submitted',
  TotalUnsubmitted = 'Total unsubmitted',
  TotalPassed = 'Total passed',
  TotalDisqualified = 'Total disqualified',
  TotalSubmittedRegionNominations = 'Submitted region',
  TotalUnsubmittedRegionNominations = 'Unsubmitted region',
  TotalPassedRegionNominations = 'Passed region',
  TotalDisqualifiedRegionNominations = 'Disqualified region',
}

const AwardProgramChartsImpl: React.FC = () => {
  const {
    userStore,
    programDashboardPageUiStore,
  } = useStores();

  const shouldRenderNominationTypeChart = (): boolean => {
    return userStore.isAdmin;
  }

  const shouldRenderNominationSubmissionChart = (): boolean => {
    return (userStore.isAdmin || userStore.isGroupLead || userStore.isRegionLead);
  }

  const shouldRenderNominationResultChart = (): boolean => {
    return (userStore.isAdmin || userStore.isGroupLead);
  }

  const renderChart = (chartData: ChartProps): JSX.Element => {
    const total = sumBy(chartData.chartData ?? [], (point) => point.data ?? 0);
    const isEmpty = total === 0;

    const displayedChartData: ChartProps = isEmpty
      ? {
        chartData: [
          {
            legend: 'No data',
            data: 1,
            color: '#E0E0E0',
          },
        ],
      }
      : chartData;

    return (
      <FlexBox
        direction='column'
        className='donut-chart-card'
      >
        <DonutChart
          data={displayedChartData}
          className='donut-chart-card__chart'
          innerRadius={CHART_INNER_RADIUS}
          width={CHART_WIDTH}
          height={CHART_HEIGHT}
          hideLegend
          hideTooltip={isEmpty}
        />
        <div>
          {chartData.chartData?.map((point, index) => (
            <FlexBox
              key={index}
              aligns='center'
              className='chart-legend'
            >
              <div
                className='chart-legend__dot'
                style={{ backgroundColor: point.color }}
              />
              <Text
                size={200}
                weight='semibold'
              >
                {point.legend}: {point.data}
              </Text>
            </FlexBox>
          ))}
        </div>
      </FlexBox>
    );
  }

  const renderNominationTypeChart = (): JSX.Element => {
    return renderChart({
      chartData: [
        {
          legend: ChartLegend.CategoryNominations,
          data: programDashboardPageUiStore.numberOfCategoryAwardNominations,
          color: Green,
        },
        {
          legend: ChartLegend.CountryNominations,
          data: programDashboardPageUiStore.numberOfCountryAwardNominations,

          color: LightGreen,
        },
        {
          legend: ChartLegend.RegionNominations,
          data: programDashboardPageUiStore.numberOfRegionAwardNominations,
          color: DarkGreen,
        },
      ],
    });
  };

  const renderSubmissionStatisticChart = (): JSX.Element => {
    return renderChart({
      chartData: [
        {
          legend: ChartLegend.TotalSubmitted,
          data: programDashboardPageUiStore.numberOfSubmittedNominations,
          color: Cyan,
        },
        {
          legend: ChartLegend.TotalUnsubmitted,
          data: programDashboardPageUiStore.numberOfInProgressNominations,
          color: LightCyan,
        },
        {
          legend: ChartLegend.TotalSubmittedRegionNominations,
          data: programDashboardPageUiStore.numberOfSubmittedRegionAwardNominations,
          color: DarkCyan,
        },
        {
          legend: ChartLegend.TotalUnsubmittedRegionNominations,
          data: programDashboardPageUiStore.numberOfInprogressRegionAwardNominations,
          color: LightBlue,
        },
      ],
    });
  };

  const renderNominationResultChart = (): JSX.Element => {
    return renderChart({
      chartData: [
        {
          legend: ChartLegend.TotalPassed,
          data: programDashboardPageUiStore.numberOfPassedNominations,
          color: Purple,
        },
        {
          legend: ChartLegend.TotalDisqualified,
          data: programDashboardPageUiStore.numberOfDisqualifiedNominations,
          color: LightPurple,
        },
        {
          legend: ChartLegend.TotalPassedRegionNominations,
          data: programDashboardPageUiStore.numberOfPassedRegionAwardNominations,
          color: DarkPurple,
        },
        {
          legend: ChartLegend.TotalDisqualifiedRegionNominations,
          data: programDashboardPageUiStore.numberOfDisqualifiedRegionAwardNominations,
          color: MidDarkPurple,
        },
      ],
    });
  };

  return (
    <FlexBox
      className='award-program-charts'
    >
      {shouldRenderNominationTypeChart() && renderNominationTypeChart()}
      {shouldRenderNominationSubmissionChart() && renderSubmissionStatisticChart()}
      {shouldRenderNominationResultChart() && renderNominationResultChart()}
    </FlexBox>
  );
}

export const AwardProgramCharts = observer(AwardProgramChartsImpl);
